"use client"

import { useState, useEffect, Suspense } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { authService } from "@/services/auth"
import { TurnstileWidget } from "@/components/landing/turnstile-widget"
import { useTurnstile } from "@/hooks/use-turnstile"
import { Loader2, Mail, Lock, User, ArrowLeft, Eye, EyeOff, ExternalLink, Copy, Check } from "lucide-react"
import { toast } from "sonner"
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "@/components/ui/input-otp"

export default function RegisterPage() {
    return (
        <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen bg-muted/40 p-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        }>
            <RegisterForm />
        </Suspense>
    )
}

function RegisterForm() {
    const router = useRouter()
    const searchParams = useSearchParams()
    const next = searchParams?.get('next')
    const redirectUrl = next ? decodeURIComponent(next) : '/dashboard'

    const [isLoading, setIsLoading] = useState(false)
    const captcha = useTurnstile()
    const [step, setStep] = useState<'register' | 'otp'>('register')
    const [isFBBrowser, setIsFBBrowser] = useState(false)
    const [isAndroid, setIsAndroid] = useState(false)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        const ua = navigator.userAgent || ""
        setIsFBBrowser(/FBAN|FBAV|FB_IAB|FB4A|FBIOS|Instagram|Messenger|\[FB_IAB\]/.test(ua))
        setIsAndroid(/Android/i.test(ua))
    }, [])

    const handleOpenInBrowser = () => {
        const url = window.location.href
        if (isAndroid) {
            const host = url.replace(/^https?:\/\//, '')
            window.location.href = `intent://${host}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`
        } else {
            navigator.clipboard?.writeText(url).then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2500)
            }).catch(() => {
                toast.info("Copy the link from your address bar and paste it into Safari.")
            })
        }
    }

    // Form State
    const [fullName, setFullName] = useState("")
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [otp, setOtp] = useState("")

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!fullName || !email || !password) {
            toast.error("Please fill in all fields")
            return
        }

        if (password.length < 8) {
            toast.error("Password must be at least 8 characters")
            return
        }

        setIsLoading(true)
        try {
            await authService.register({
                full_name: fullName,
                email,
                password,
                turnstile_token: captcha.token,
            })
            toast.success("Account created successfully", {
                description: "Please check your email for the verification code."
            })
            setStep('otp')
        } catch (error: any) {
            if (error?.response?.status === 428) {
                captcha.reset()
                toast.error("Verification needed", {
                    description:
                        error.response?.data?.detail ||
                        "Please complete the verification check. If it will not load, disable your ad blocker and refresh.",
                })
                setIsLoading(false)
                return
            }
            console.error("Registration failed:", error)
            toast.error("Registration failed", {
                description: error.response?.data?.detail || "Could not create account"
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault()

        if (otp.length !== 6) {
            toast.error("Please enter a valid 6-digit code")
            return
        }

        setIsLoading(true)
        try {
            await authService.verifyOtp(email, otp)
            toast.success("Account verified successfully")
            window.location.href = redirectUrl
        } catch (error: any) {
            console.error("Verification failed:", error)
            toast.error("Verification failed", {
                description: error.response?.data?.detail || "Invalid or expired code"
            })
        } finally {
            setIsLoading(false)
        }
    }



    return (
        <div className="flex items-center justify-center min-h-screen bg-muted/40 p-4 relative overflow-hidden">
            {/* Ambient Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -left-[10%] w-[70vw] h-[70vw] rounded-full bg-primary/5 blur-[100px] animate-blob" />
                <div className="absolute -bottom-[20%] -right-[10%] w-[70vw] h-[70vw] rounded-full bg-blue-500/5 blur-[100px] animate-blob animation-delay-2000" />
            </div>

            <div className="w-full sm:max-w-[450px] md:max-w-[500px] bg-background/60 backdrop-blur-xl border-white/20 p-6 md:p-8 rounded-[1.5rem] shadow-2xl border relative z-10 transition-all duration-300 hover:shadow-primary/5">
                <div className="grid gap-5">
                    <div className="grid gap-2 text-center">
                        <h1 className="text-3xl font-bold tracking-tight">
                            {step === 'register' ? "Create an account" : "Verify your email"}
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            {step === 'register'
                                ? "Enter your email below to create your account"
                                : `We've sent a 6-digit code to ${email}`}
                        </p>
                    </div>

                    {step === 'register' ? (
                        <>
                            <form onSubmit={handleRegister} className="grid gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="name" className="text-sm font-medium text-muted-foreground">Full Name</Label>
                                    <div className="relative group">
                                        <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="name"
                                            placeholder="John Doe"
                                            className="pl-9 h-10 bg-muted/50 border-none rounded-lg focus-visible:ring-1 focus-visible:ring-primary/50"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            disabled={isLoading}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid gap-2">
                                    <Label htmlFor="email" className="text-sm font-medium text-muted-foreground">Email Address</Label>
                                    <div className="relative group">
                                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="name@example.com"
                                            className="pl-9 h-10 bg-muted/50 border-none rounded-lg focus-visible:ring-1 focus-visible:ring-primary/50"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={isLoading}
                                            required
                                        />
                                    </div>
                                </div>


                                <div className="grid gap-2">
                                    <Label htmlFor="password" className="text-sm font-medium text-muted-foreground">Password</Label>
                                    <div className="relative group">
                                        <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            className="pl-9 pr-9 h-10 bg-muted/50 border-none rounded-lg focus-visible:ring-1 focus-visible:ring-primary/50 transition-all duration-200"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            disabled={isLoading}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                    <p className="text-sm text-muted-foreground font-medium">
                                        Must be at least 8 characters long
                                    </p>
                                </div>

                                {captcha.siteKey && (
                                    <div className="mt-2">
                                        <TurnstileWidget
                                            siteKey={captcha.siteKey}
                                            theme="auto"
                                            onVerify={captcha.onVerify}
                                            onExpire={captcha.onExpire}
                                            onError={captcha.onError}
                                        />
                                        {captcha.failed && (
                                            <p className="mt-2 text-center text-xs text-muted-foreground">
                                                The verification check could not load. You can still
                                                try to create your account.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-10 mt-2 rounded-lg bg-primary hover:bg-primary/90 shadow-md font-bold text-sm"
                                    disabled={isLoading || captcha.blocking}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creating account...
                                        </>
                                    ) : (
                                        "Sign Up with Email"
                                    )}
                                </Button>
                            </form>

                            {isFBBrowser ? (
                                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 grid gap-3">
                                    <p className="text-sm text-amber-700 dark:text-amber-400 font-medium leading-snug text-center">
                                        Google sign-up is disabled in this browser for security.
                                    </p>
                                    {isAndroid ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="w-full h-10 rounded-lg border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 text-sm font-medium"
                                            onClick={handleOpenInBrowser}
                                        >
                                            <ExternalLink className="mr-2 h-4 w-4" />
                                            Open in Chrome
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="w-full h-10 rounded-lg border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 text-sm font-medium"
                                                onClick={handleOpenInBrowser}
                                            >
                                                {copied ? (
                                                    <><Check className="mr-2 h-4 w-4" />Link Copied!</>
                                                ) : (
                                                    <><Copy className="mr-2 h-4 w-4" />Copy Link</>
                                                )}
                                            </Button>
                                            <p className="text-xs text-amber-600 dark:text-amber-500 text-center leading-relaxed">
                                                Tap <span className="font-bold">···</span> (top right) → <span className="font-bold">Open in external browser</span>, or paste the copied link into Safari.
                                            </p>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <Separator className="w-full" />
                                        </div>
                                        <div className="relative flex justify-center text-sm font-medium">
                                            <span className="bg-card px-3 text-muted-foreground">
                                                Or continue with
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1">
                                        <Button
                                            variant="outline"
                                            className="w-full h-10 rounded-lg border-border hover:bg-muted/50 text-sm font-medium"
                                            onClick={() => {
                                                const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID
                                                const redirectUri = process.env.NEXT_PUBLIC_GOOGLE_REDIRECT_URI || `${window.location.origin}/auth/google/callback`
                                                const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile`
                                                window.location.href = authUrl
                                            }}
                                            disabled={isLoading}
                                        >
                                            <svg className="mr-2 h-3.5 w-3.5" viewBox="0 0 24 24">
                                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                                <path d="M1 1h22v22H1z" fill="none" />
                                            </svg>
                                            Google
                                        </Button>
                                    </div>
                                </>
                            )}

                            <p className="px-6 text-center text-sm text-muted-foreground">
                                By signing up, you agree to our{" "}
                                <Link href="/terms" className="text-primary font-semibold hover:underline underline-offset-4">
                                    Terms
                                </Link>{" "}
                                and{" "}
                                <Link href="/privacy" className="text-primary font-semibold hover:underline underline-offset-4">
                                    Privacy
                                </Link>
                                .
                            </p>

                            <p className="text-center text-sm text-muted-foreground mt-1">
                                Already have an account?{" "}
                                <Link href={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="font-bold text-primary hover:underline">
                                    Sign in
                                </Link>
                            </p>
                        </>
                    ) : (
                        <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-right-10 duration-300">
                            <div className="flex justify-center">
                                <InputOTP
                                    maxLength={6}
                                    value={otp}
                                    onChange={(value) => setOtp(value)}
                                >
                                    <InputOTPGroup className="gap-2">
                                        <InputOTPSlot index={0} className="rounded-lg border-border w-10 h-10 text-lg font-bold" />
                                        <InputOTPSlot index={1} className="rounded-lg border-border w-10 h-10 text-lg font-bold" />
                                        <InputOTPSlot index={2} className="rounded-lg border-border w-10 h-10 text-lg font-bold" />
                                        <InputOTPSlot index={3} className="rounded-lg border-border w-10 h-10 text-lg font-bold" />
                                        <InputOTPSlot index={4} className="rounded-lg border-border w-10 h-10 text-lg font-bold" />
                                        <InputOTPSlot index={5} className="rounded-lg border-border w-10 h-10 text-lg font-bold" />
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>

                            <Button
                                className="w-full h-10 rounded-lg bg-primary hover:bg-primary/90 font-bold text-sm"
                                onClick={handleVerifyOtp}
                                disabled={isLoading || otp.length !== 6}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Verifying...
                                    </>
                                ) : (
                                    "Verify & Continue"
                                )}
                            </Button>

                            <div className="text-center">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setStep('register')}
                                    className="gap-2 text-muted-foreground hover:text-primary text-sm font-medium"
                                >
                                    <ArrowLeft className="h-3.5 w-3.5" />
                                    Back to registration
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
